// @trace impl designs/charter-form.md
/* The ASI Project Charter form as a field catalog (designs/charter-form.md
   CH1): one list drives validation, storage and rendering, so a field added
   here appears in the form, is accepted by the validator and is stored, with
   no second list to keep in step. Sections and labels follow the Word form
   (Scythe_Project_Charter.docx, Jul 27 2026). People-shaped fields (PM,
   sponsor/SME, team, support resources, process owner), milestones and the
   financials are deliberately NOT here — see the design's placement table. */
import type { Stamp, UserRef } from "./organizer-roles";

export interface CharterFieldDef {
  id: CharterFieldId;
  label: string;
  kind: "text" | "choice";
  /* the choice field's closed list — the template's own words */
  options?: readonly string[];
  hint?: string;
}

export interface CharterSection {
  title: string;
  fields: readonly CharterFieldDef[];
}

export const CHARTER_FIELD_IDS = [
  "confidentiality",
  "organizationalUnits",
  "problem",
  "purpose",
  "businessCase",
  "goals",
  "expectedDeliverables",
  "requirements",
  "withinScope",
  "outsideScope",
  "specialNeeds",
  "keyStakeholders",
  "finalCustomer",
  "expectedBenefits",
  "risks",
  "constraints",
  "assumptions",
] as const;
export type CharterFieldId = (typeof CHARTER_FIELD_IDS)[number];

export const CONFIDENTIALITY_OPTIONS = ["Open", "Restricted", "Executive Only"] as const;

export const CHARTER_SECTIONS: readonly CharterSection[] = [
  {
    title: "General project information",
    fields: [
      {
        id: "confidentiality",
        label: "Confidentiality classification",
        kind: "choice",
        options: CONFIDENTIALITY_OPTIONS,
      },
      {
        id: "organizationalUnits",
        label: "Organizational units",
        kind: "text",
        hint: "As the charter writes them, one per line — e.g. Advanced Prototyping Team (lead)",
      },
    ],
  },
  {
    title: "Project overview",
    fields: [
      { id: "problem", label: "Problem or issue", kind: "text" },
      { id: "purpose", label: "Purpose", kind: "text" },
      { id: "businessCase", label: "Business case / OKR alignment", kind: "text" },
      { id: "goals", label: "Goals and metrics", kind: "text" },
      { id: "expectedDeliverables", label: "Expected deliverables", kind: "text" },
      { id: "requirements", label: "High-level requirements", kind: "text" },
    ],
  },
  {
    title: "Project scope",
    fields: [
      { id: "withinScope", label: "Within scope", kind: "text" },
      { id: "outsideScope", label: "Outside of scope", kind: "text" },
    ],
  },
  {
    title: "Resources",
    fields: [
      {
        id: "specialNeeds",
        label: "Special needs",
        kind: "text",
        hint: "Equipment, shipments, travel — people are recorded on Roles & RACI",
      },
    ],
  },
  {
    title: "Benefits and customers",
    fields: [
      { id: "keyStakeholders", label: "Key stakeholders", kind: "text" },
      { id: "finalCustomer", label: "Final customer", kind: "text" },
      { id: "expectedBenefits", label: "Expected benefits", kind: "text" },
    ],
  },
  {
    title: "Risks, constraints and assumptions",
    fields: [
      { id: "risks", label: "Risks", kind: "text" },
      { id: "constraints", label: "Constraints", kind: "text" },
      { id: "assumptions", label: "Assumptions", kind: "text" },
    ],
  },
];

export const CHARTER_FIELDS: readonly CharterFieldDef[] = CHARTER_SECTIONS.flatMap((s) => s.fields);

const fieldDef = (id: string): CharterFieldDef | undefined => CHARTER_FIELDS.find((f) => f.id === id);

/* A field the caller supplies (CH2): the value, the unconfirmed flag that
   makes it an orange assumption, and optionally the document it was
   transcribed from — blank means Entered in app. */
export interface CharterFieldInput {
  value: string;
  unconfirmed: boolean;
  source?: string;
}

/* As served: the field carries its own stamp (CH3) — where THIS value came
   from and when it became true, not when the row was last touched. */
export interface CharterField {
  value: string;
  unconfirmed: boolean;
  recorded: Stamp;
}

/* Every key present; null = not recorded (red). */
export type Charter = Record<CharterFieldId, CharterField | null>;

/* Absent key = KEEP the recorded field, explicit null = clear (PS9/DIR8 per
   field). */
export type CharterInput = Partial<Record<CharterFieldId, CharterFieldInput | null>>;

/* the source a stamp reads when the recorder names none; projects.ts
   re-exports it so every caller keeps one name */
export const ENTERED_SRC = "Entered in app";

export const MAX_VALUE = 4000;
export const MAX_SOURCE = 200;

export function emptyCharter(): Charter {
  return Object.fromEntries(CHARTER_FIELD_IDS.map((id) => [id, null])) as Charter;
}

/* A stamp carried forward onto a new revision must be a COPY all the way
   down: append-only history that shares a mutable object is not history
   (AA4). The actor is the part that was missed — one shared `by` reference
   between two revisions is still shared state (PR #63 review, Aug 26 2026).
   Shared with projects.ts's charge-code and span revisions. */
export const copyStamp = (stamp: Stamp): Stamp => ({
  ...stamp,
  ...(stamp.by ? { by: { ...stamp.by } } : {}),
});

/* Mirrors spanDateRevision (project-span PS2) exactly: an unchanged value +
   unconfirmed carries its previous stamp forward as a copy; a changed or new
   value is stamped now with the named source. A source named on an
   UNCHANGED field is ignored — the stamp answers where this value came from,
   and this value came from the earlier save (CH3). */
export function charterFieldRevision(
  input: CharterFieldInput | null | undefined,
  previous: CharterField | null,
  by?: UserRef,
): CharterField | null {
  if (!input) return null;
  const value = input.value.trim();
  if (previous && previous.value === value && previous.unconfirmed === input.unconfirmed)
    return { ...previous, recorded: copyStamp(previous.recorded) };
  return {
    value,
    unconfirmed: input.unconfirmed,
    recorded: {
      source: input.source?.trim() || ENTERED_SRC,
      date: new Date().toISOString(),
      ...(by ? { by } : {}),
    },
  };
}

/* The whole form: absent charter or absent key keeps the recorded field. */
export function charterRevision(
  input: CharterInput | null | undefined,
  previous: Charter | null,
  by?: UserRef,
): Charter {
  const base = previous ?? emptyCharter();
  const out = emptyCharter();
  for (const id of CHARTER_FIELD_IDS) {
    const prev = base[id];
    const supplied = input ? input[id] : undefined;
    out[id] =
      supplied === undefined
        ? prev && { ...prev, recorded: copyStamp(prev.recorded) }
        : charterFieldRevision(supplied, prev, by);
  }
  return out;
}

/* Body rules (CH4). Returns the reason to refuse, or null. Shape checks keep
   a malformed body a 400 reason, never a thrown 500. A top-level null is
   refused rather than read as "keep" or "clear all": a field is cleared by
   ITS null, and silently keeping on a record-level null would be the
   asymmetry CH2 never stated (review, Sep 16 2026). */
export function charterReason(charter: unknown): string | null {
  if (charter === undefined) return null;
  if (typeof charter !== "object" || charter === null || Array.isArray(charter))
    return "the charter must be an object of fields";
  for (const [id, field] of Object.entries(charter as Record<string, unknown>)) {
    const def = fieldDef(id);
    if (!def) return `"${id}" is not a charter field`;
    if (field === null) continue;
    if (typeof field !== "object" || Array.isArray(field))
      return `${def.label} must be an object with value and unconfirmed`;
    const { value, unconfirmed, source } = field as Partial<CharterFieldInput>;
    if (typeof unconfirmed !== "boolean") return `${def.label}'s unconfirmed flag must be true or false`;
    if (typeof value !== "string" || !value.trim())
      return `${def.label} must not be blank — leave it not recorded instead`;
    if (value.trim().length > MAX_VALUE) return `${def.label} must be at most ${MAX_VALUE} characters`;
    if (source !== undefined && (typeof source !== "string" || source.length > MAX_SOURCE))
      return `a source must be text of at most ${MAX_SOURCE} characters`;
    if (def.kind === "choice" && !def.options?.includes(value.trim()))
      return `the ${def.label.toLowerCase()} must be ${listOr(def.options ?? [])}`;
  }
  return null;
}

const listOr = (options: readonly string[]): string =>
  options.length < 2
    ? options.join("")
    : `${options.slice(0, -1).join(", ")} or ${options[options.length - 1]}`;
